import { schoolByIdQueryDomainSelector, makeNullSafeDomainGetter, createIsLoadingSelector, schoolSearchSelector, makeByIdsDictionarySelector } from './common';
import { createSelector } from 'reselect';
import bbox from '@turf/bbox';
import { boundsToTileRange } from 'components/map/utils';
import { ISearchPoiVariables, DealType, MarketplaceType, AmenitiesMap, ISearchParameters } from 'utils/entities';
import { SortField, SortDirection } from 'components/listing-sort/types';
import { MAX_ZOOM, prepareSecureRoomType } from 'store/sagas/routing/handlers/search/utils';
import { PoiWithSearchFields } from 'utils/resolutionPreferenceGroup';
import { flow } from 'lodash';
import { mergeWithInitialState } from 'components/filters/utils';


export const schoolByIdSelector = makeNullSafeDomainGetter(schoolByIdQueryDomainSelector, 'getSchoolById');
export const isSchoolByIdLoadingSelector = createIsLoadingSelector(schoolByIdQueryDomainSelector);

export const schoolSearchListSelector = makeNullSafeDomainGetter(schoolSearchSelector, 'searchPoiV2');
export const isSchoolSearchLoadingSelector = createIsLoadingSelector(schoolSearchSelector);

const EMPTY: PoiWithSearchFields[] = [];

export const searchBySchoolListSelector = flow(schoolSearchListSelector, (search) => search && search.poi ? search.poi : EMPTY);
export const schoolSearchPoiDictionarySelector = makeByIdsDictionarySelector(searchBySchoolListSelector);

const SCHOOL_LISTINGS_OFFSET = 10;

export const schoolPageCityDocIdSelector = flow(schoolByIdSelector, (schoolData) => schoolData && schoolData.cityDocId ? schoolData.cityDocId : null);
export const schoolPageAreaDocIdSelector = flow(schoolByIdSelector, (schoolData) => schoolData ? (schoolData.neighborhoodDocId || schoolData.cityDocId) : null);
export const schoolPageDocIdSelector = flow(schoolByIdSelector, (schoolData) => schoolData && schoolData.docId ? schoolData.docId : null);

function makeEmptyResidentialSearchParams(docId: string): ISearchParameters {
  const {
    fee,
    seller,
    amenities,
    areaRange,
    conditions,
    propertyTypes,
    projectDiscount,
    priceChanges: { priceDrop, underPriceEstimation },
  } = mergeWithInitialState({}, DealType.Buy, MarketplaceType.Residential);


  const amenitiesMap = amenities.reduce<AmenitiesMap>((acc, amenity) => ({
    ...acc,
    [amenity]: true,
  }), {});

  const { restAmenities, secureRoomType } = prepareSecureRoomType(amenitiesMap);

  return {
    noFee: fee,
    dealType: DealType.Buy,
    numberOfEmployeesRange: [ null, null ],
    commercialAmenities: {},
    qualityClass: [],
    discountedProjects: projectDiscount,
    roomsRange: [ null, null ],
    bathsRange: [ null, null ],
    floorRange: [ null, null ],
    areaRange,
    buildingClass: propertyTypes,
    sellerType: seller,
    generalCondition: conditions,
    ppmRange: [ null, null ],
    priceRange: [ null, null ],
    monthlyTaxRange: [ null, null ],
    amenities: restAmenities,
    secureRoomType,
    sort: [
      {
        field: SortField.Geometry,
        order: SortDirection.Asc,
        reference: null,
        docIds: [ docId ],
      },
    ],
    priceDrop,
    underPriceEstimation,
  };
}

export const schoolAreaSearchVariablesSelector = createSelector([
  schoolByIdSelector,
], (schoolData): ISearchPoiVariables => {

  if (schoolData && schoolData.registrationZones) {
    const [ west, south, east, north ] = bbox({
      type: 'FeatureCollection',
      features: schoolData.registrationZones.map(rz => ({
        type: 'Feature',
        geometry: {
          ...rz.geometry,
          type: 'MultiPolygon',
        },
      })),
    });

    const tileRanges =  [ boundsToTileRange([ [ west, south ], [ east, north ] ], MAX_ZOOM) ];

    return {
      ...makeEmptyResidentialSearchParams(schoolData.docId),
      tileRanges,
      poiTypes: [ 'bulletin' ],
      searchContext: 'marketplace',
      isCommercialRealEstate: false,
      cursor: {
        seenProjects: null,
        bulletinsOffset: 0,
      },
      offset: 0,
      limit: SCHOOL_LISTINGS_OFFSET,
    };

  }

  return null;
});
